import React, { useState, useEffect } from 'react'
import ReactDOM from 'react-dom'
import UnfoldMore from '@Images/sidebar_unfold_more.svg'
import useClickOutsideListener from '@Utils/UseClickOutsideListener'
import router from '@Site/Router'
import Tooltip from '@SiteComponents/Tooltip'
import classnames from 'classnames'
import map from 'lodash/map'

import {
  IWorkspace,
  ISetCurrentWorkspacePayload,
} from '@SiteModules/Workspace/types'

import './WorkspaceDropdown.scss'

const DELAY_FOR_SHOWING_ID = 3000 // ms

interface WorkspaceDropdownProps {
  readonly currentWorkspaceId: number
  readonly currentWorkspaceName: string
  readonly workspaces: IWorkspace[]
  setCurrentWorkspace(payload: ISetCurrentWorkspacePayload): void
}

const WorkspaceDropdown: React.FC<WorkspaceDropdownProps> = props => {
  const {
    workspaces,
    currentWorkspaceId,
    currentWorkspaceName,
  } = props

  const [showDropdown, setShowDropdown] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipTimeout, setTooltipTimeout] = useState<ReturnType<typeof setTimeout> | null>(null)

  useClickOutsideListener('WorkspaceDropdown__element', setShowDropdown, showDropdown)

  useEffect(() => {
    if (showTooltip) {
      setShowTooltip(false)
    }
  }, [currentWorkspaceId])

  const shouldShowTooltip = showTooltip && workspaces.length > 1

  const toggleDropdown = () => {
    if (!hasMoreThanOneWorkspace) { return }

    setShowDropdown(!showDropdown)
  }

  const displayTooltip = () => {
    if (tooltipTimeout) { return }

    const newTooltipTimeout = setTimeout(() => {
      setShowTooltip(true)
    }, DELAY_FOR_SHOWING_ID)
    setTooltipTimeout(newTooltipTimeout)
  }

  const hideTooltip = () => {
    if (!tooltipTimeout) { return }

    setShowTooltip(false)
    clearTimeout(tooltipTimeout)
    setTooltipTimeout(null)
  }

  const workspacesList = map(workspaces, workspace => {
    const isCurrentWorkspace = workspace.id === currentWorkspaceId

    const changeWorkspace = () => {
      if (isCurrentWorkspace) { return }

      props.setCurrentWorkspace({ workspaceId: workspace.id })
      router.navigate('/')
    }

    return (
      <li
        key={workspace.id}
        className={classnames(
          'WorkspaceDropdown__dropdown-item',
          { 'WorkspaceDropdown__dropdown-item-active': isCurrentWorkspace }
        )}
        onClick={changeWorkspace}
      >
        <div className="WorkspaceDropdown__dropdown-item-name">
          {workspace.attributes.name}
        </div>
      </li>
    )
  })

  const renderDropdown = () => {
    return ReactDOM.createPortal(
      (
        <div className="WorkspaceDropdown__dropdown WorkspaceDropdown__element">
          <ul className="WorkspaceDropdown__dropdown-items">
            {workspacesList}
          </ul>
        </div>
      ),
      document.getElementById('sidebar')!
    )
  }

  const hasMoreThanOneWorkspace = workspaces.length > 1

  return (
    <div
      className="WorkspaceDropdown WorkspaceDropdown__element"
      onClick={toggleDropdown}
      onMouseEnter={displayTooltip}
      onMouseLeave={hideTooltip}
    >
      <div className={
        classnames(
          'WorkspaceDropdown__body',
          { 'WorkspaceDropdown__body-with-dropdown': hasMoreThanOneWorkspace },
          { 'WorkspaceDropdown__body-active': showDropdown }
        )
      }>
        <div className="WorkspaceDropdown__current-workspace">
          {currentWorkspaceName}
        </div>
        {hasMoreThanOneWorkspace &&
          (
            <UnfoldMore />
          )
        }
        {showDropdown && renderDropdown()}
      </div>
      <div className="WorkspaceDropdown__tooltip">
        {shouldShowTooltip && (
          <Tooltip
            text={`Workspace ID: ${currentWorkspaceId}`}
            position={'bottom'}
          />
        )}
      </div>
    </div>
  )
}

export default WorkspaceDropdown
